import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface CreateProjectDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onCreateProject: (projectName: string) => Promise<void> | void;
}

export function CreateProjectDialog({ isOpen, onClose, onCreateProject }: CreateProjectDialogProps) {
    const [projectName, setProjectName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (projectName.trim() && !isCreating) {
            setIsCreating(true);
            try {
                await onCreateProject(projectName.trim());
                setProjectName('');
                onClose();
            } catch (error) {
                console.error('Failed to create project:', error);
            } finally {
                setIsCreating(false);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && !isCreating) {
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Create New Project</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="project-name">
                            Project Name
                        </Label>
                        <Input
                            ref={inputRef}
                            id="project-name"
                            type="text"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isCreating}
                            placeholder="Enter project name..."
                        />
                    </div>

                    <div className="flex items-center justify-end space-x-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isCreating}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!projectName.trim() || isCreating}
                        >
                            {isCreating && <Loader2 size={16} className="animate-spin" />}
                            <span>{isCreating ? 'Creating...' : 'Create Project'}</span>
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
} 